import { showElementFromDisplayNone, hideElementToDisplayNone } from '@utils/helpers';
import { CustomWindow } from "@utils/models/customwindow";

// Ensure CustomWindow is available as a value
declare let window: CustomWindow;
let currentActiveSubMenu: HTMLElement | null = null;
let currentActiveMenuButton: HTMLElement | null = null;

export const useDesktopMenuService = () => {
  if (!window.SGI) {
    window.SGI = {
      handleDesktopKeyboardNavigation: null
    };
  }
  const desktopMediaQuery = window.matchMedia('(min-width: 1271px)');
  const duration = 200;
  const subMenuOpenClass = "sub-menu--open";
  const subMenuClosedClass = "sub-menu--closed";
  const megaMenuOpenClass = "menu__item--selected-parent";
  const subMenuIconClass = "icon--rotate";

  const toggleMegaMenu = (button: HTMLElement, e: Event | null = null): void => {
    if (e !== null) {
      e.preventDefault();
    }
    const curentMenuItem = button.parentElement as HTMLElement; // Parent is the <li> containing the button
    const currentSubMenu = button.nextElementSibling as HTMLDivElement; // Sibling is the <ul> containing the submenu
    currentActiveSubMenu = currentSubMenu;
    currentActiveMenuButton = button;
    const alreadyOpen = currentSubMenu.classList.contains(subMenuOpenClass);
    const openAndCloseClassesActive = alreadyOpen && currentSubMenu.classList.contains(subMenuClosedClass);
    // Only get the first level submenus
    const firstLevelSubmenus: HTMLElement[] = Array.from(document.querySelectorAll(".menu > .menu__list > .menu__item > .sub-menu--open"));

    if (openAndCloseClassesActive) {
      button.setAttribute("aria-expanded", "true");
      // Show the first level submenu
      // Does not work as the other levels so for now, animation is done here.
      currentSubMenu.classList.remove(subMenuClosedClass);
      currentSubMenu.classList.add(subMenuOpenClass);
      currentSubMenu.style.overflow = "hidden";
      currentSubMenu.style.height = "0px";
      currentSubMenu.style.opacity = "0";
      
      // Force a reflow
      currentSubMenu.offsetHeight; // eslint-disable-line
      
      const fullHeight = currentSubMenu.scrollHeight + "px";
      currentSubMenu.style.transition = `all ${duration}ms ease-in-out`;
      currentSubMenu.style.height = fullHeight;
      currentSubMenu.style.opacity = "1";
      
      setTimeout(() => {
        currentSubMenu.style.overflow = "auto";
        currentSubMenu.style.height = "auto";
      }, duration);

      document.body.classList.add("menu-open");
    } else if (alreadyOpen) {
      button.setAttribute("aria-expanded", "false");
      curentMenuItem.classList.remove(megaMenuOpenClass);

      // Hide the first level submenu
      const fullHeight = currentSubMenu.scrollHeight + "px";
      currentSubMenu.style.height = fullHeight;
      currentSubMenu.style.opacity = "1";
      currentSubMenu.offsetHeight; // eslint-disable-line
      
      currentSubMenu.style.transition = `all ${duration}ms ease-in-out`;
      currentSubMenu.style.height = "0px";
      currentSubMenu.style.overflow = "hidden";
      currentSubMenu.style.opacity = "0";
      
      setTimeout(() => {
        currentSubMenu.classList.remove(subMenuOpenClass);
        currentSubMenu.style.overflow = "auto";
        currentSubMenu.classList.add(subMenuClosedClass);
      }, duration);

      document.body.classList.remove("menu-open");
      if (window.SGI && window.SGI.handleDesktopKeyboardNavigation) {
        document.removeEventListener('keydown', window.SGI.handleDesktopKeyboardNavigation);
      }
    } else {
      button.setAttribute("aria-expanded", "true");
      document.body.classList.add("menu-open");

      // Hide all other open submenus
      firstLevelSubmenus.forEach(item => {
        item.parentElement?.classList.remove(megaMenuOpenClass);
        item.classList.remove(subMenuOpenClass);
        setTimeout(() => { item.classList.add(subMenuClosedClass); }, duration);
      });

      setTimeout(() => {
        currentSubMenu.style.overflow = "hidden";
        currentSubMenu.style.height = "0px";
        currentSubMenu.style.opacity = "0";
        currentSubMenu.classList.remove(subMenuClosedClass);
        currentSubMenu.classList.add(subMenuOpenClass);
        
        // Force a reflow
        currentSubMenu.offsetHeight; // eslint-disable-line
        
        const fullHeight = currentSubMenu.scrollHeight + "px";
        currentSubMenu.style.transition = `all ${duration}ms ease-in-out`;
        currentSubMenu.style.height = fullHeight;
        currentSubMenu.style.opacity = "1";
        
        setTimeout(() => {
          currentSubMenu.style.overflow = "auto";
          currentSubMenu.style.height = "auto";
        }, duration);
      }, 1);

      curentMenuItem.classList.add(megaMenuOpenClass);
      if (window.SGI && window.SGI.handleDesktopKeyboardNavigation) {
        document.addEventListener('keydown', window.SGI.handleDesktopKeyboardNavigation);
      }
    }
  };

  const toggleSubMenuItemDesktop = (button: HTMLElement, e: Event | null = null): void => {
    if (e !== null) {
      e.preventDefault();
    }

    // Parent is <a>, sibling is the <div> we want
    const currentSubMenu: HTMLElement = button.parentElement?.nextElementSibling as HTMLElement;
    const currentIcon = button.getElementsByClassName("icon")[0];

    if (currentSubMenu.classList.contains(subMenuOpenClass)) {
      hideElementToDisplayNone(currentSubMenu, subMenuOpenClass, duration);
      currentIcon.classList.remove(subMenuIconClass);
      button.setAttribute("aria-expanded", "false");
    } else {
      showElementFromDisplayNone(currentSubMenu, subMenuOpenClass, duration);
      currentIcon.classList.add(subMenuIconClass);
      button.setAttribute("aria-expanded", "true");
    }
  };

  // Close on escape -------------------------------
  const closeOnEsc = (e: KeyboardEvent): void => {
    if (desktopMediaQuery.matches && !e.defaultPrevented && e.key === 'Escape') {
      e.preventDefault();
      closeOpenMenuItems();
    }
  };

  // Close when clicking outside -------------------------------
  const closeWhenClickingOutside = (e: MouseEvent): void => {
    if (desktopMediaQuery.matches && e.target instanceof Element) {
      const headerContainer = document.querySelector('.header__container') as HTMLDivElement;

      if (!headerContainer.contains(e.target)) {
        if (document.body.classList.contains('menu-open') && currentActiveMenuButton) {
          toggleMegaMenu(currentActiveMenuButton as HTMLElement);
        }
      }
    }
  };

  const closeOpenMenuItems = (): void => {
    // Close open menus
    Array.from(document.getElementsByClassName(subMenuOpenClass)).forEach(item => {
      item.classList.remove(subMenuOpenClass);
    });

    // Toggle aria-expanded attribute
    Array.from(document.getElementsByClassName('sub-menu__sub-menu-toggle')).forEach(item => {
      item.setAttribute("aria-expanded", "false");
    });

    // Rotate icons back
    const menu = document.querySelector('nav.menu') as HTMLElement;
    Array.from(menu.getElementsByClassName(`${subMenuIconClass}`)).forEach(item => {
      item.classList.remove(subMenuIconClass);
    });

    // Reset active menu items
    Array.from(document.getElementsByClassName(megaMenuOpenClass)).forEach(item => {
      item.classList.remove(megaMenuOpenClass);
    });

    if (window.SGI && window.SGI.handleDesktopKeyboardNavigation) {
      document.removeEventListener('keydown', window.SGI.handleDesktopKeyboardNavigation);
    }

    if (currentActiveMenuButton) {
      currentActiveMenuButton.setAttribute("aria-expanded", "false");
      currentActiveMenuButton.focus();
    }

    document.body.classList.remove("menu-open");
  };

  window.SGI.handleDesktopKeyboardNavigation = (e: KeyboardEvent) => {
    // Get all focusable elements in the currently open menu
    if (!currentActiveSubMenu) {
      return;
    }

    const focusable = Array.from(currentActiveSubMenu.querySelectorAll('button, a')) as HTMLElement[];
    const visibleFocusable = focusable.filter(el => el.offsetParent !== null);
    if (visibleFocusable.length) {
      const firstElement = visibleFocusable[0];
      const lastElement = visibleFocusable[visibleFocusable.length - 1];

      if (e.key === 'Tab') {
        if ((e.shiftKey && document.activeElement === firstElement) || (!e.shiftKey && document.activeElement === lastElement)) {
          closeOpenMenuItems();
          e.preventDefault();
        }
      }
    }
  };

  return {
    desktopMediaQuery,
    closeOnEsc,
    closeWhenClickingOutside,
    toggleMegaMenu,
    toggleSubMenuItemDesktop,
    closeOpenMenuItems,
    subMenuClosedClass,
    subMenuOpenClass
  };
};